import {Component, computed, ElementRef, Inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../services/confirm-dialog-service/confirm-dialog/confirm-dialog.component';
import {TtmStatus} from '../../classes/ttmstatus';
import {Ttm} from '../../classes/ttm';
import {CodaltComponent} from '../codalt/codalt.component';
import {FormControl, FormGroup} from '@angular/forms';
import {TtmService} from '../../services/ttm.service';
import {TtmImage} from '../../classes/ttmimage';
import {TtmImageService} from '../../services/ttm-image.service';
import * as turf from '@turf/turf';
import {ApiErrorResponse} from '../../services/api/api.service';
import {FileUploadFailDialogComponent} from '../file-upload-fail-dialog/file-upload-fail-dialog.component';
import {Utils} from '../../utils.class';
import {DeviceOrientationService} from '../../services/device-orientation.service';

/**
 * Dialog steps enumeration
 * Step 1: StatusSelection - User selects a TTM status
 * Step 2: Weighting - User answers yes/no questions for each weighting field (if ask_weighting is true)
 * Step 3: ImageSelection - User selects how to add a photo
 */
enum DialogStep {
    StatusSelection = 1,
    Weighting = 2,
    ImageSelection = 3,
}

export enum TTMStatus {
    GOEDGEKEURD = 'approved',
    DEMONTAGE = 'demontage',
    ONTWERP = 'drawed',
    AFPLAKKING_VERWIJDERD = 'no_longer_taped',
    AFGEPLAKT = 'taped',
    GECORRIGEERD = 'corrected',
    GEPLAATST = 'placed',
    OPGEDRAAID = 'turned_up',
    AFGEDRAAID = 'turned_down',
    OPGEHAALD = 'collected',
    AFWIJKEND = 'deficiency',
    VERLOREN = 'missing',
    AF_TE_PLAKKEN = 'to_be_taped',
    OVERIG = 'other',
    PERMANENT = 'permanent',
}

@Component({
    selector: 'app-ttm-status-dialog',
    templateUrl: './ttm-status-dialog.component.html',
    styleUrl: './ttm-status-dialog.component.scss',
})
export class TtmStatusDialogComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('imageGallery') imageGallery: ElementRef<HTMLInputElement>;
    latestImage: TtmImage;
    distance: number;
    loading: 'latest' | 'gallery' | 'camera' | 'none' = null;

    fgWeightings = new FormGroup<any>({});
    weightingFields = ['weighting_one', 'weighting_two', 'weighting_three'];
    availableWeightingFields: string[] = [];
    currentWeightingIndex = signal<number>(0);
    currentWeightingField = computed(() => this.availableWeightingFields[this.currentWeightingIndex()]);
    currentWeightingName = computed(() => {
        const field = this.currentWeightingField();
        return field ? this.data.ttm.type[field + '_name'] || '' : '';
    });
    heading: number | null = null;

    currentStep = signal<DialogStep>(DialogStep.StatusSelection);

    fcStatus = new FormControl();
    statuses: TtmStatus[];
    statusValue = toSignal(this.fcStatus.valueChanges, {initialValue: this.fcStatus.value});
    selectedStatus = computed(() => this.statuses?.find((s) => s.key === this.statusValue()));

    readonly totalSteps = Object.keys(DialogStep).length / 2;

    constructor(
        public dialogRef: MatDialogRef<ConfirmDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ttm: Ttm;
            status: TtmStatus;
            gpsLocation: { lat: number; lng: number };
        },
        private ttmService: TtmService,
        private ttmImageService: TtmImageService,
        private matDialog: MatDialog,
        private deviceOrientationService: DeviceOrientationService,
    ) {
        super();

        this.fcStatus.setValue(this.data.ttm.status_key);

        this.weightingFields.forEach((field) => {
            if (this.data.ttm.type[field + '_name']) {
                this.availableWeightingFields.push(field);
                this.fgWeightings.addControl(field, new FormControl(this.data.ttm.type[field]));
            }
        });
    }

    ngOnInit() {
        // Start listening to device orientation
        this.deviceOrientationService.startListening();

        this.subscriptions.add(
            this.deviceOrientationService.getHeading().subscribe((heading) => {
                this.heading = heading;
            })
        );

        this.subscriptions.add(
            this.ttmService.statuses().subscribe((statuses) => {
                this.statuses = statuses.data;
            }),
        );
        this.subscriptions.add(
            this.ttmImageService.latestImage().subscribe((latestImage) => {
                this.latestImage = latestImage.data;
            }),
        );

        this.distance = turf.distance(
            turf.point(this.data.ttm.entities[0].gnd.coordinates[0] as any),
            turf.point([this.data.gpsLocation?.lng, this.data.gpsLocation?.lat]),
            {units: 'meters'},
        );
    }

    override ngOnDestroy() {
        // Stop listening to device orientation
        this.deviceOrientationService.stopListening();
        super.ngOnDestroy();
    }

    uploadFile(event, gallery = false) {
        this.loading = gallery ? 'gallery' : 'camera';
        event.stopPropagation();
        event.preventDefault();
        const files = event.srcElement.files;
        if (files.length > 0) {
            const newImg = new TtmImage();
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.upload(file, newImg);
            }
        }
    }

    private upload(file, newImg: TtmImage) {
        this.loading = this.loading ?? 'camera';
        Utils.resize(
            (resizedImage) => {
                const heading = this.heading;
                this.subscriptions.add(
                    this.ttmImageService
                        .uploadImage(resizedImage, this.fcStatus.value, this.data.ttm.id, this.data.gpsLocation, heading)
                        .subscribe(
                            (ttmImage) => {
                                Object.assign(newImg, ttmImage.data);
                                this.data.ttm.ttm_images_count = (this.data.ttm.ttm_images_count ?? 0) + 1;
                                this.dialogRef.close(this.fcStatus.value);
                            },
                            (error: ApiErrorResponse) => {
                                this.loading = null;
                                const subs = this.matDialog
                                    .open(FileUploadFailDialogComponent, {
                                        panelClass: 'confirm-dialog',
                                        disableClose: true,
                                        data: {file, error},
                                        maxWidth: '450px',
                                    })
                                    .afterClosed()
                                    .subscribe((retry) => {
                                        if (retry) {
                                            this.upload(file, newImg);
                                        } else {
                                            this.dialogRef.close(false);
                                        }
                                        subs.unsubscribe();
                                    });
                            },
                        ),
                );
                this.imageGallery.nativeElement.value = null;
            },
            file,
            1920,
        );
    }

    cancel() {
        this.dialogRef.close(false);
    }

    useLatest() {
        this.loading = 'latest';
        this.subscriptions.add(
            this.ttmImageService.useLatest(this.fcStatus.value, this.latestImage.id, this.data.ttm.id, this.data.gpsLocation).subscribe({
                next: () => {
                    this.data.ttm.ttm_images_count = (this.data.ttm.ttm_images_count ?? 0) + 1;
                    this.dialogRef.close(this.fcStatus.value);
                },
                error: () => {
                    this.loading = null;
                }
            }),
        );
    }

    noPicture() {
        this.loading = 'none';
        this.subscriptions.add(
            this.ttmService.updateStatus(this.data.ttm.id, this.fcStatus.value, this.data.gpsLocation).subscribe({
                next: () => {
                    this.dialogRef.close(this.fcStatus.value);
                },
                error: () => {
                    this.loading = null;
                },
            }),
        );
    }

    selectStatus(status: TtmStatus) {
        this.fcStatus.setValue(status.key);
        this.nextStep();
    }

    nextStep() {
        if (this.currentStep() === DialogStep.StatusSelection) {
            const selectedStatus = this.statuses.find((s) => s.key === this.fcStatus.value);

            if (selectedStatus?.ask_weighting && this.availableWeightingFields.length > 0) {
                this.currentWeightingIndex.set(0);
                this.currentStep.set(DialogStep.Weighting);
            } else {
                this.currentStep.set(DialogStep.ImageSelection);
            }
        } else if (this.currentStep() === DialogStep.Weighting) {
            if (this.currentWeightingIndex() < this.availableWeightingFields.length - 1) {
                this.currentWeightingIndex.update(i => i + 1);
            } else {
                this.currentStep.set(DialogStep.ImageSelection);
            }
        }
    }

    previousStep() {
        if (this.currentStep() === DialogStep.Weighting) {
            if (this.currentWeightingIndex() > 0) {
                this.currentWeightingIndex.update(i => i - 1);
            } else {
                this.currentStep.set(DialogStep.StatusSelection);
            }
        } else if (this.currentStep() === DialogStep.ImageSelection) {
            const selectedStatus = this.statuses.find((s) => s.key === this.fcStatus.value);

            if (selectedStatus?.ask_weighting && this.availableWeightingFields.length > 0) {
                this.currentWeightingIndex.set(this.availableWeightingFields.length - 1);
                this.currentStep.set(DialogStep.Weighting);
            } else {
                this.currentStep.set(DialogStep.StatusSelection);
            }
        } else if (this.currentStep() > DialogStep.StatusSelection) {
            this.currentStep.update(s => s - 1);
        }
    }

    setWeightingValue(value: boolean) {
        const currentField = this.currentWeightingField();
        this.fgWeightings.get(currentField)?.setValue(value);
        this.subscriptions.add(
            this.ttmService.updateWeighting(this.data.ttm.id, currentField, value).subscribe(
                (ttm) => {
                    this.data.ttm[currentField] = ttm.data[currentField];
                    this.nextStep();
                },
                () => {
                },
            ),
        );
    }
}
